import React, { useEffect, useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { buildBlogSlugPath } from "../../utils/slugify";

const API_URL = "/admin/api/blogs.php";

// blog_content comes back as an HTML string (e.g. "<p>...</p>").
// Strip the tags and trim it down for a clean card excerpt.
function htmlToExcerpt(html, maxLength = 140) {
  if (!html) return "";
  const text = html.replace(/<[^>]*>/g, "").trim();
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + "…";
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    axios
      .get(API_URL)
      .then((res) => {
        if (!isMounted) return;
        const data = res.data;
        const list = data.blogs ?? (Array.isArray(data) ? data : []);
        setPosts(list);
        setLoading(false);
      })
      .catch((err) => {
        if (!isMounted) return;
        const message =
          err.response?.data?.message ||
          err.message ||
          "Something went wrong";
        setError(message);
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Blog - Websiteseo</title>
        <meta
          name="description"
          content={
            "Stay updated with the latest news and insights in web development, SEO, and digital marketing."
          }
        />
        <meta
          name="keywords"
          content={
            "web development blog, SEO blog, digital marketing blog, website development news"
          }
        />
        <link rel="canonical" href={"https://www.websiteseo.ae/blog"} />
      </Helmet>

      {/* Scoped styles for the blog card grid */}
      <style>{`
        .blog-card-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 30px;
          justify-content: flex-start;
        }
        .blog-card {
          flex: 1 1 300px;
          max-width: 360px;
          background: #fff;
          border-radius: 14px;
          overflow: hidden;
          box-shadow: 0 4px 18px rgba(0,0,0,0.07);
          transition: transform 0.25s ease, box-shadow 0.25s ease;
          display: flex;
          flex-direction: column;
        }
        .blog-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 14px 30px rgba(0,0,0,0.12);
        }
        .blog-card-img-wrap {
          width: 100%;
          height: 210px;
          overflow: hidden;
        }
        .blog-card-img-wrap img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.35s ease;
        }
        .blog-card:hover .blog-card-img-wrap img {
          transform: scale(1.06);
        }
        .blog-card-body {
          padding: 22px 24px 26px;
          display: flex;
          flex-direction: column;
          flex-grow: 1;
        }
        .blog-card-date {
          font-size: 13px;
          letter-spacing: 0.3px;
          text-transform: uppercase;
          color: #8a8a8a;
          margin-bottom: 8px;
          font-weight: 600;
        }
        .blog-card-title {
          font-size: 19px;
          font-weight: 700;
          line-height: 1.35;
          margin-bottom: 12px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .blog-card-excerpt {
          font-size: 14.5px;
          color: #666;
          line-height: 1.6;
          margin-bottom: 20px;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .blog-card-cta {
          margin-top: auto;
        }
      `}</style>

      <div>
        {/* HERO / BREADCRUMB BANNER */}
        <div className="inner-page-banner-wrap">
          <div className="container">
            <h3>Blog</h3>
            <ul>
              <li>
                <Link to="/">Home</Link>
              </li>
              <li>Blog</li>
            </ul>
          </div>
        </div>

        {/* INTRO */}
        <section className="ct-01 content-section division pt-80 pb-0">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-md-10 col-lg-8 text-center">
                <span className="module-subtitle subtitle-line">Insights</span>
                <h2 className="fs-46 mb-3">Latest Articles &amp; Insights</h2>
                <p className="fs-18">
                  Tips, guides, and updates on SEO, web design, and digital marketing
                  from the WebsiteSEO team.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* BLOG CARDS */}
        <section className="py-60 features-6 features-section division">
          <div className="container">
            {loading && (
              <div className="row justify-content-center">
                <div className="col-md-8 text-center">
                  <p className="fs-18">Loading articles…</p>
                </div>
              </div>
            )}

            {!loading && error && (
              <div className="row justify-content-center">
                <div className="col-md-8 text-center">
                  <p className="fs-18">
                    Couldn&apos;t load blog posts right now. ({error})
                  </p>
                </div>
              </div>
            )}

            {!loading && !error && posts.length === 0 && (
              <div className="row justify-content-center">
                <div className="col-md-8 text-center">
                  <p className="fs-18">No blog posts yet — check back soon.</p>
                </div>
              </div>
            )}

            {!loading && !error && posts.length > 0 && (
              <div className="blog-card-grid">
                {posts.map((post) => (
                  <div className="blog-card" key={post.blog_id}>
                    {post.blog_image && (
                      <div className="blog-card-img-wrap">
                        <img src={post.blog_image} alt={post.blog_title} />
                      </div>
                    )}
                    <div className="blog-card-body">
                      {post.blog_date && (
                        <span className="blog-card-date">
                          {formatDate(post.blog_date)}
                        </span>
                      )}
                      <h5 className="blog-card-title">{post.blog_title}</h5>
                      <p className="blog-card-excerpt">
                        {htmlToExcerpt(post.blog_content)}
                      </p>
                      <Link
                        to={`/blog/${buildBlogSlugPath(post.blog_title, post.blog_id)}`}
                        className="btn r-08 btn--tra-black hover--plum blog-card-cta"
                      >
                        Read More
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
}

export default Blog;
